import React, { useRef, useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import { formatPrice } from '../../../../rbts/helpers';

type Props = {
  strDate: string;
  data: any;
  handleClose: (updateGrid: boolean) => void;
};

const loadImageAsBase64 = async (url: string): Promise<string | null> => {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 4000);
    const response = await fetch(url, { signal: controller.signal });
    clearTimeout(timeout);
    if (!response.ok) return null;
    const blob = await response.blob();
    if (!blob.type.startsWith('image/')) return null;
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const ConcilationPdf: React.FC<Props> = ({ strDate, data, handleClose }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [totalElectronicBalance, setTotalElectronicBalance] = useState<number>(0);
  const [totalBank, setTotalBank] = useState<number>(0);
  const [totalsupplier, setTotalsupplier] = useState<number>(0);
  const [totalRawMaterial, setTotalRawMaterial] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [supplierName, setSupplierName] = useState<string>('');
  const [user, setUser] = useState<string>('');
  const [detail, setDetail] = useState<any>([]);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<boolean>(false);

  const itemsPerPage = 10;

  const generatePDF = async (): Promise<string | null> => {
    if (!detail || !detail.detalle) return null;
    const totalPages = Math.ceil(detail.detalle.length / itemsPerPage) || 1;
    let flagForToPrintFinalData: any;
    let yPosition = 100;

    const logoBase64 = await loadImageAsBase64(process.env.PUBLIC_URL + '/media/logos/logtickett.png');

    const doc = new jsPDF({ format: 'letter' });

    const generatePage = (pageNumber: any) => {
      const startIndex = (pageNumber - 1) * itemsPerPage;
      const endIndex = Math.min(startIndex + itemsPerPage, detail.detalle.length);
      const pageData = detail.detalle.slice(startIndex, endIndex);

      doc.setTextColor('#000');
      doc.setFontSize(15);
      doc.text('Conciliación con proveedor', 20, 30);

      const logoWidth = 55;
      const logoHeight = 15;
      if (logoBase64) {
        try {
          doc.addImage(logoBase64, 'PNG', 148, 18, logoWidth, logoHeight);
        } catch {
          // si el logo falla, continuar sin él
        }
      }

      doc.setFontSize(12);
      doc.setTextColor('#000');
      doc.text(`Fecha administrativa: ${strDate}`, 20, 40);

      const cellHeight = 10;
      const startX = 20;
      const startY = 60;

      const headerData = [
        { nombre: 'Nombre del puesto' },
        { nombre: supplierName },
      ];
      const textColors = ['#000', '#000'];
      headerData.forEach((row, index) => {
        const rowY = startY + (index + 1) * cellHeight;
        doc.setTextColor(textColors[index]);
        doc.text(row.nombre, startX, rowY);
      });

      // Encabezado de la tabla
      doc.setFontSize(9);
      doc.setTextColor('#000');
      doc.text('Productos',    14, yPosition);
      doc.text('Precio',       62, yPosition);
      doc.text('Cantidad',     86, yPosition);
      doc.text('Saldo elect.', 106, yPosition);
      doc.text('Banco',        140, yPosition);
      doc.text('Proveedor',    158, yPosition);
      doc.text('M. prima',     177, yPosition);
      doc.text('Total',        198, yPosition);

      let currentY = yPosition + 10;
      pageData.forEach((item: any) => {
        doc.setFontSize(9);
        const maxChars = item.es_cortesia ? 14 : 22;
        const nombreProducto =
          item.producto_nombre && item.producto_nombre.length > maxChars
            ? item.producto_nombre.substring(0, maxChars) + '...'
            : item.producto_nombre || '';
        doc.setTextColor('#000');
        doc.text(nombreProducto, 14, currentY);
        if (item.es_cortesia) {
          const nameWidth = doc.getTextWidth(nombreProducto);
          doc.setFontSize(8);
          doc.setTextColor('#4A5DDC');
          doc.text('Cortesía', 14 + nameWidth + 2, currentY);
          doc.setFontSize(9);
          doc.setTextColor('#000');
        }
        doc.text(formatPrice(item.precio || 0, 'es-MX', 'MXN', 2, 2).toString(), 62, currentY);
        doc.text(item.cantidad.toString(), 86, currentY);
        doc.text(formatPrice(item.saldo_electronico || 0, 'es-MX', 'MXN', 2, 2).toString(), 106, currentY);
        doc.text(formatPrice(item.monto_banco || 0, 'es-MX', 'MXN', 2, 2).toString(), 140, currentY);
        doc.text(formatPrice(item.monto_punto_venta || 0, 'es-MX', 'MXN', 2, 2).toString(), 158, currentY);
        currentY += 10;
      });

      if (flagForToPrintFinalData === totalPages) {
        doc.setFontSize(9);
        doc.text('Total', 62, currentY);
        doc.text(totalAmount.toString(), 86, currentY);
        doc.text(formatPrice(totalElectronicBalance || 0, 'es-MX', 'MXN', 2, 2).toString(), 106, currentY);
        doc.text(formatPrice(totalBank || 0, 'es-MX', 'MXN', 2, 2).toString(), 140, currentY);
        doc.text(formatPrice(totalsupplier || 0, 'es-MX', 'MXN', 2, 2).toString(), 158, currentY);
        doc.text(formatPrice(totalRawMaterial || 0, 'es-MX', 'MXN', 2, 2).toString(), 177, currentY);
        doc.text(formatPrice(total || 0, 'es-MX', 'MXN', 2, 2).toString(), 198, currentY);
        doc.text(supplierName, 33, 260);
        doc.text(user, 163, 260);
      }

      return doc;
    };

    for (let page = 1; page <= totalPages; page++) {
      flagForToPrintFinalData = page;
      generatePage(page);
      if (page < totalPages) {
        doc.addPage();
      }
    }

    const pdfBlob = doc.output('blob') as Blob;
    return URL.createObjectURL(pdfBlob);
  };

  // Liberar el Blob URL anterior antes de asignar uno nuevo
  useEffect(() => {
    return () => {
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfBlobUrl]);

  // Cargar datos cuando llegan del modal
  useEffect(() => {
    if (data) {
      const {
        total_cantidad,
        total_saldo_electronico,
        total_banco,
        total_punto_venta,
        total_materia_prima,
        total_a_pagar,
        punto_venta,
        user: userData,
      }: any = data;
      setTotalAmount(total_cantidad);
      setTotalElectronicBalance(total_saldo_electronico);
      setTotalBank(total_banco);
      setTotalsupplier(total_punto_venta);
      setTotalRawMaterial(total_materia_prima);
      setTotal(total_a_pagar);
      setSupplierName(punto_venta ? punto_venta?.nombre : '');
      setUser(userData ? `${userData?.first_name} ${userData?.father_last_name}` : '');
      setDetail(data);
    } else {
      // Limpiar la Blob URL al cerrar para liberar memoria
      setPdfBlobUrl(null);
      setDetail([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Generar el PDF automáticamente cuando los datos están listos
  useEffect(() => {
    if (detail && Array.isArray(detail.detalle)) {
      setPdfError(false);
      generatePDF()
        .then((url) => {
          if (url) {
            setPdfBlobUrl(url);
          } else {
            setPdfError(true);
          }
        })
        .catch((err) => {
          console.error('Error generating PDF:', err);
          setPdfError(true);
        });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail]);

  return (
    <>
      <div
        ref={contentRef}
        className="d-flex flex-row-fluid flex-center rounded"
      >
        <div className="w-100">
          {/* Visor PDF embebido — la barra nativa del browser incluye descarga e impresión */}
          {pdfError ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: '300px' }}
            >
              <span className="text-danger fw-bold" style={{ textAlign: 'center' }}>
                No se pudo generar el PDF. Intenta de nuevo.
              </span>
            </div>
          ) : pdfBlobUrl ? (
            <iframe
              src={pdfBlobUrl}
              title="Vista previa — Conciliación con proveedor"
              width="100%"
              height="620px"
              style={{ border: 'none', display: 'block' }}
            />
          ) : (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: '300px' }}
            >
              <span
                className="indicator-progress text-white"
                style={{ display: 'block', textAlign: 'center' }}
              >
                Generando vista previa del PDF...
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </div>
          )}

          {/* Botón cerrar */}
          <div className="d-flex justify-content-end p-3">
            <button
              onClick={() => {
                setPdfBlobUrl(null);
                handleClose(false);
              }}
              className="btn btn-primary"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
export { ConcilationPdf };
